import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getLatestArticles, type Article } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

function RelatedCard({ article }: { article: Article }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex gap-3 p-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all duration-200"
    >
      <div className="w-28 h-20 flex-shrink-0 rounded-md overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {article.title}
        </h4>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
          <span>{article.category}</span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleBySlug(id || '');
  const category = article ? getCategoryBySlug(article.categorySlug) : null;
  const allArticles = getLatestArticles(20);
  const relatedArticles = article
    ? allArticles.filter((a) => a.id !== article.id).slice(0, 4)
    : [];

  if (!article) {
    return <NotFound />;
  }

  const articleTitle = `${article.title} - 4688.tv`;
  const articleUrl = `/article/${article.slug}`;

  return (
    <>
      <SEOHead
        title={articleTitle}
        description={article.excerpt}
        keywords={`4688.tv ${article.category},${article.tags.join(',')},${article.title}`}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.coverImage}
        article={{
          publishedTime: article.publishDate,
          tags: article.tags,
        }}
      />

      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.publishDate,
          author: { '@type': 'Organization', name: '4688.tv' },
          publisher: { '@type': 'Organization', name: '4688.tv' },
        }}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://4688.tv' },
            ...(category
              ? [
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: category.name,
                    item: `https://4688.tv/category/${category.slug}`,
                  },
                ]
              : []),
            {
              '@type': 'ListItem',
              position: category ? 3 : 2,
              name: article.title,
              item: `https://4688.tv${articleUrl}`,
            },
          ],
        }}
      />

      <main className="pt-20">
        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {(article.readCount / 10000).toFixed(1)}万 阅读
              </span>
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="flex items-center gap-1.5 text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-folder-line" />
                  {article.category}
                </Link>
              )}
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {article.duration}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="w-full aspect-video rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none mb-16 text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 max-w-4xl mx-auto pb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedArticles.map((a) => (
                <RelatedCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}